import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Menu, X, ChevronDown, Phone, Download } from 'lucide-react';
import { Button } from './ui/button';
import BrochureDownloadModal from './BrochureDownloadModal';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [purDropdownOpen, setPurDropdownOpen] = useState(false);
  const [designStudioDropdownOpen, setDesignStudioDropdownOpen] = useState(false);
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  // Track scroll for header background
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  const products = [
    { name: 'Acrylic Laminated Boards', path: '/products/acrylic-boards' },
    { name: 'CNC Designer Doors', path: '/products/cnc-doors' },
    { name: 'Decorative Wall Panels', path: '/products/wall-panels' },
    { name: 'Mica Laminated Doors', path: '/products/mica-doors' },
    { name: 'Veneer Doors (Without Nail)', path: '/products/veneer-doors' }
  ];

  const handleCatalogueDownload = () => {
    setBrochureModalOpen(true);
  };

  // Dynamic header styles based on scroll and page
  const headerBg = scrolled || !isHomePage
    ? 'bg-white/95 backdrop-blur-xl border-b border-black/5'
    : 'bg-transparent';
  
  const textColor = scrolled || !isHomePage
    ? 'text-[#004534]'
    : 'text-white';
  
  const textColorSecondary = scrolled || !isHomePage
    ? 'text-gray-700 hover:text-[#004534]'
    : 'text-white/80 hover:text-white';

  return (
    <motion.header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <motion.div
              className="relative overflow-hidden grid justify-items-start"
              style={{ transformOrigin: 'left center' }}
              initial={reduceMotion ? false : { opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0, scale: scrolled ? 0.9 : 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ scale: scrolled ? 0.93 : 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              <img
                src="/crilam-logo-oval.png"
                alt="CriLam Decorative"
                className={`col-start-1 row-start-1 h-16 lg:h-20 w-auto object-contain transition-opacity duration-500 ${scrolled || !isHomePage ? 'opacity-0' : 'opacity-100'}`}
              />
              <img
                src="/crilam-logo-green.png"
                alt=""
                aria-hidden="true"
                className={`col-start-1 row-start-1 h-16 lg:h-20 w-auto object-contain transition-opacity duration-500 ${scrolled || !isHomePage ? 'opacity-100' : 'opacity-0'}`}
              />
              {!reduceMotion && (
                <motion.span
                  className="pointer-events-none absolute inset-y-0 left-0 w-1/3"
                  style={{
                    background: 'linear-gradient(105deg, transparent 0%, rgba(212,175,55,0.65) 40%, rgba(255,255,255,0.9) 50%, rgba(212,175,55,0.65) 60%, transparent 100%)',
                    filter: 'blur(2px)'
                  }}
                  initial={{ x: '-150%' }}
                  animate={{ x: '400%' }}
                  transition={{ delay: 0.7, duration: 1.0, ease: 'easeInOut' }}
                />
              )}
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium tracking-wide transition-colors ${
                isActive('/') ? textColor : textColorSecondary
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>

            {/* Products Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors ${
                  location.pathname.startsWith('/products')
                    ? textColor
                    : textColorSecondary
                }`}
                data-testid="nav-products"
              >
                Products
                <ChevronDown className={`h-4 w-4 transition-transform ${productsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {productsDropdownOpen && (
                  <motion.div 
                    className="absolute top-full left-0 mt-2 w-64 bg-white/95 backdrop-blur-xl shadow-xl border border-black/5 py-2 overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {products.map((product, index) => (
                      <motion.div
                        key={product.path}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={product.path}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#004534] hover:text-white transition-colors"
                          data-testid={`nav-product-${product.path.split('/').pop()}`}
                        >
                          {product.name}
                        </Link>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/applications"
              className={`text-sm font-medium tracking-wide transition-colors ${
                isActive('/applications') ? textColor : textColorSecondary
              }`}
              data-testid="nav-applications"
            >
              Applications
            </Link>

            {/* CriLam PUR-Bond™ Technology Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setPurDropdownOpen(true)}
              onMouseLeave={() => setPurDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors ${
                  location.pathname.startsWith('/pur') || 
                  location.pathname.startsWith('/why-factory') ||
                  location.pathname.startsWith('/ask-your') ||
                  location.pathname.startsWith('/customer-stories') ||
                  location.pathname.startsWith('/application-benefits')
                    ? textColor
                    : textColorSecondary
                }`}
                data-testid="nav-pur-technology"
              >
                CriLam PUR-Bond™ Technology
                <ChevronDown className={`h-4 w-4 transition-transform ${purDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {purDropdownOpen && (
                  <motion.div 
                    className="absolute top-full left-0 mt-2 w-64 bg-white/95 backdrop-blur-xl shadow-xl border border-black/5 py-2 overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {[
                      { name: 'What is CriLam PUR-Bond™ Technology?', path: '/pur-technology' },
                      { name: 'Why Factory-Laminated?', path: '/why-factory-laminated' },
                      { name: 'Benefits by Application', path: '/application-benefits' },
                      { name: 'Customer Stories', path: '/customer-stories' },
                      { name: 'Ask Your Carpenter', path: '/ask-your-carpenter' }
                    ].map((item, index) => (
                      <motion.div
                        key={item.path}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={item.path}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#004534] hover:text-white transition-colors"
                          data-testid={`nav-${item.path.slice(1)}`}
                        >
                          {item.name}
                        </Link>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Design Studio Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setDesignStudioDropdownOpen(true)}
              onMouseLeave={() => setDesignStudioDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors ${
                  location.pathname.startsWith('/design-gallery') ||
                  location.pathname.startsWith('/ai-visualizer') ||
                  location.pathname.startsWith('/door-designer') ||
                  location.pathname.startsWith('/product-recommender') ||
                  location.pathname.startsWith('/match-my-doors') ||
                  location.pathname.startsWith('/nail-free-advantage')
                    ? textColor
                    : textColorSecondary
                }`}
                data-testid="nav-design-studio"
              >
                Design Studio
                <ChevronDown className={`h-4 w-4 transition-transform ${designStudioDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {designStudioDropdownOpen && (
                  <motion.div 
                    className="absolute top-full left-0 mt-2 w-64 bg-white/95 backdrop-blur-xl shadow-xl border border-black/5 py-2 overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {[
                      { name: 'Design Gallery', path: '/design-gallery', icon: '🎨' },
                      { name: 'AI Visualizer', path: '/ai-visualizer', icon: '✨' },
                      { name: 'AI Door Designer', path: '/door-designer', icon: '🚪' },
                      { name: 'Match My Doors (AI)', path: '/match-my-doors', icon: '🧩' },
                      { name: 'Nail-Free Advantage', path: '/nail-free-advantage', icon: '🛡' },
                      { name: 'Product Recommender', path: '/product-recommender', icon: '🎯' }
                    ].map((item, index) => (
                      <motion.div
                        key={item.path}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={item.path}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#004534] hover:text-white transition-colors"
                          data-testid={`nav-${item.path.slice(1)}`}
                        >
                          <span className="mr-2">{item.icon}</span>
                          {item.name}
                        </Link>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium tracking-wide transition-colors ${
                isActive('/about') ? textColor : textColorSecondary
              }`}
              data-testid="nav-about"
            >
              About Us
            </Link>

            <Link
              to="/dealer"
              className={`text-sm font-medium tracking-wide transition-colors ${
                isActive('/dealer') ? textColor : textColorSecondary
              }`}
              data-testid="nav-dealer"
            >
              Dealer / Distributor
            </Link>

            <Link
              to="/contact"
              className={`text-sm font-medium tracking-wide transition-colors ${
                isActive('/contact') ? textColor : textColorSecondary
              }`}
              data-testid="nav-contact"
            >
              Contact
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <motion.button
              onClick={handleCatalogueDownload}
              className={`flex items-center px-5 py-2.5 text-sm font-medium border transition-all ${
                scrolled || !isHomePage
                  ? 'border-[#004534] text-[#004534] hover:bg-[#004534] hover:text-white'
                  : 'border-white/30 text-white hover:bg-white/10'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="header-download-catalogue"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Catalogue
            </motion.button>
            <a href="tel:8298297475">
              <motion.button 
                className="flex items-center px-5 py-2.5 text-sm font-medium bg-[#D4AF37] text-[#0A0D0C] hover:bg-[#C19B2E] transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="header-call"
              >
                <Phone className="h-4 w-4 mr-2" />
                Call Us
              </motion.button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className={`h-6 w-6 ${scrolled || !isHomePage ? 'text-[#004534]' : 'text-white'}`} />
            ) : (
              <Menu className={`h-6 w-6 ${scrolled || !isHomePage ? 'text-[#004534]' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              className="lg:hidden py-6 bg-white/95 backdrop-blur-xl border-t border-black/5"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <nav className="flex flex-col gap-4">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-base font-medium text-gray-700 hover:text-[#004534] transition-colors"
                >
                  Home
                </Link>

                <div className="flex flex-col gap-2">
                  <span className="text-base font-medium text-gray-700">Products</span>
                  <div className="pl-4 flex flex-col gap-2">
                    {products.map((product) => (
                      <Link
                        key={product.path}
                        to={product.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-sm text-gray-600 hover:text-[#004534] transition-colors"
                      >
                        {product.name}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link
                  to="/applications"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-base font-medium text-gray-700 hover:text-[#004534] transition-colors"
                >
                  Applications
                </Link>

                <div className="flex flex-col gap-2">
                  <span className="text-base font-medium text-gray-700">CriLam PUR-Bond™ Technology</span>
                  <div className="pl-4 flex flex-col gap-2">
                    {[
                      { name: 'What is CriLam PUR-Bond™ Technology?', path: '/pur-technology' },
                      { name: 'Why Factory-Laminated?', path: '/why-factory-laminated' },
                      { name: 'Benefits by Application', path: '/application-benefits' },
                      { name: 'Customer Stories', path: '/customer-stories' },
                      { name: 'Ask Your Carpenter', path: '/ask-your-carpenter' }
                    ].map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-sm text-gray-600 hover:text-[#004534] transition-colors"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-base font-medium text-gray-700">Design Studio</span>
                  <div className="pl-4 flex flex-col gap-2">
                    {[
                      { name: '🎨 Design Gallery', path: '/design-gallery' },
                      { name: '✨ AI Visualizer', path: '/ai-visualizer' },
                      { name: '🚪 AI Door Designer', path: '/door-designer' },
                      { name: '🧩 Match My Doors (AI)', path: '/match-my-doors' },
                      { name: '🛡 Nail-Free Advantage', path: '/nail-free-advantage' },
                      { name: '🎯 Product Recommender', path: '/product-recommender' }
                    ].map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-sm text-gray-600 hover:text-[#004534] transition-colors"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-base font-medium text-gray-700 hover:text-[#004534] transition-colors"
                >
                  About Us
                </Link>

                <Link
                  to="/dealer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-base font-medium text-gray-700 hover:text-[#004534] transition-colors"
                >
                  Dealer / Distributor
                </Link>

                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-base font-medium text-gray-700 hover:text-[#004534] transition-colors"
                >
                  Contact
                </Link>

                <div className="flex flex-col gap-3 pt-4">
                  <button
                    onClick={() => {
                      handleCatalogueDownload();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center justify-center px-5 py-3 text-sm font-medium border border-[#004534] text-[#004534] hover:bg-[#004534] hover:text-white transition-colors w-full"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Catalogue
                  </button>
                  <a href="tel:8298297475" className="w-full">
                    <button className="flex items-center justify-center px-5 py-3 text-sm font-medium bg-[#D4AF37] text-[#0A0D0C] hover:bg-[#C19B2E] transition-colors w-full">
                      <Phone className="h-4 w-4 mr-2" />
                      Call Us
                    </button>
                  </a>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Brochure Download Modal */}
      <BrochureDownloadModal
        isOpen={brochureModalOpen}
        onClose={() => setBrochureModalOpen(false)}
      />
    </motion.header>
  );
};

export default Header;
